/**
 * The trtMeet backend (backend/): room directory, signaller and ICE servers.
 *
 * Runtime configuration is window.TRTMEET_CONFIG, written by /config.js (see
 * index.html) — in production from the container's environment, so one build
 * serves any domain. The build-time VITE_* variables remain as the fallback.
 *
 *   backendOrigin   where the backend lives (http(s)://host[:port]); empty = this page's own origin,
 *                   which is how it is deployed: nginx and the dev server forward the backend's paths
 *   roomsOrigin     optional: the room directory elsewhere
 *   iceServers      optional: a fixed list instead of /v1/ice
 */

const env = (typeof import.meta !== 'undefined' && import.meta.env) || {};
const runtime = (typeof window !== 'undefined' && window.TRTMEET_CONFIG) || {};

const trim = (value) => String(value || '').replace(/\/+$/, '');

export const BACKEND_ORIGIN = trim(runtime.backendOrigin || env.VITE_BACKEND_ORIGIN);
/** Where the room directory lives, when it is not on the backend. */
export const ROOMS_ORIGIN_OVERRIDE = trim(runtime.roomsOrigin || env.VITE_ROOMS_ORIGIN);

function parseIceList(json) {
  if (!json) return null;
  try {
    const list = JSON.parse(json);
    return Array.isArray(list) && list.length ? list : null;
  } catch {
    console.warn('[backend] VITE_ICE_SERVERS is not valid JSON — ignoring it');
    return null;
  }
}

const STATIC_ICE =
  Array.isArray(runtime.iceServers) && runtime.iceServers.length ? runtime.iceServers : parseIceList(env.VITE_ICE_SERVERS);

/** The WebSocket URL of the signaller: the backend's origin, `/signal`. */
export function signallerUrl() {
  const base = BACKEND_ORIGIN || (typeof location !== 'undefined' ? location.origin : '');
  return `${base.replace(/^http/, 'ws')}/signal`;
}

/** Fresh credentials are fetched when the ones in hand are this far into their life. */
const REFRESH_AT = 0.5;
const FETCH_TIMEOUT_MS = 5000;
let current = STATIC_ICE ? { iceServers: STATIC_ICE, fetchedAt: Date.now(), ttl: Infinity } : null;
let inflight = null;

/** The ICE servers to hand the peer client, or undefined to let the browser use its defaults. Synchronous. */
export function cachedIceServers() {
  return current?.iceServers;
}

/**
 * Make sure `cachedIceServers()` has something good in it. Never rejects: a
 * backend that cannot be asked leaves whatever was cached.
 */
export function ensureIceServers() {
  const age = current ? (Date.now() - current.fetchedAt) / 1000 : Infinity;
  if (current && age < current.ttl * REFRESH_AT) return Promise.resolve(current.iceServers);
  inflight ??= fetchIce().finally(() => {
    inflight = null;
  });
  return inflight;
}

async function fetchIce() {
  try {
    const res = await fetch(`${BACKEND_ORIGIN}/v1/ice`, { signal: AbortSignal.timeout(FETCH_TIMEOUT_MS) });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const { iceServers, ttl } = await res.json();
    if (Array.isArray(iceServers) && iceServers.length) {
      current = { iceServers, fetchedAt: Date.now(), ttl: Number(ttl) > 0 ? Number(ttl) : 600 };
    }
  } catch (error) {
    console.warn('[backend] could not load ICE servers —', error?.message ?? error);
  }
  return current?.iceServers;
}

/** Test hook: forget what was loaded. */
export function resetBackendCache() {
  current = STATIC_ICE ? { iceServers: STATIC_ICE, fetchedAt: Date.now(), ttl: Infinity } : null;
  inflight = null;
}
